// Types for config (structure only)
export type IntegerMetaFieldConfig = {
  type: 'integer';
  key: string;
  label: string;
  min?: number;
  max?: number;
  topTagOnly?: boolean;
  thresholdTags?: { value: number; tag: string; color?: string }[];
  default?: number;
};

export type BooleanMetaFieldConfig = {
  type: 'boolean';
  key: string;
  label: string;
  checkedTag?: string;
  tagColor?: string;
};

export type MetaFieldConfig = IntegerMetaFieldConfig | BooleanMetaFieldConfig;

export interface UnitMetaConfig {
  id: string;
  name: string;
  metaFields: MetaFieldConfig[];
}

export interface ArmyConfig {
  name: string;
  faction: string;
  grandAlliance: 'Order' | 'Chaos' | 'Death' | 'Destruction';
  shortName: string;
  shortArmyName?: string;
  armyFields?: MetaFieldConfig[];
  units?: UnitMetaConfig[];
}

// Types for persisted state (values only)
export type MetaFieldValue = {
  key: string;
  value: number | boolean | undefined;
};

export type UnitMetaValue = {
  id: string;
  label?: string;
  metaFields: MetaFieldValue[];
};

export type PlayerMeta = {
  notes: string;
  armyFields: MetaFieldValue[];
  units: UnitMetaValue[];
};

export enum Army {
  BoKBGP = 'Blades of Khorne Bloodhound Gore Pilgrims',
  BoKFotBG = 'Blades of Khorne Fans of the Blood God',
  CosCC = 'Cities of Sigmar Castelite Company',
  CosFP = 'Cities of Sigmar Fusil-Platoon',
  DoKHT = 'Daughters of Kaine Heartflayer Troupe',
  KOST = 'Kharadron Overlords Skyhammer Taskforce',
  KOGT = 'Kharadron Overlords Grundstok Trailblazers',
  DoTFC = 'Disciples of Tzeentch Fluxblade Coven',
  FECCR = 'Flesh-eater Courts Carrion Retainers',
  FECCW = 'Flesh-eater Courts Charnel Watch',
  FSA = 'Fyreslayers Saga Axeband',
  GSGBMM = 'Gloomspite Gitz Bad Moon Madmob',
  GSGSH = 'Gloomspite Gitz Snarlpack Huntaz',
  HoSBotLD = 'Hedonites of Slaanesh Blades of the Lurid Dream',
  HHHH = 'Helsmiths of Hashut Helforge Host',
  IDKSH = 'Idoneth Deepkin Soulraid Hunt',
  IDKATG = 'Idoneth Deepkin Akhelian Tide Guard',
  IJBM = 'Ironjawz Bigmob',
  KBSG = 'Kruleboyz Swampskulka Gang',
  LRLGP = 'Lumineth Realm-lords Glittering Phalanx',
  MoNBH = 'Maggotkin of Nurgle Bleak Host',
  NHSH = 'Nighthaunt Slasher Host',
  NHCS = 'Nighthaunt Cursed Shaklehorde',
  OBRTRE = 'Ossiarch Bonereapers Tithe-reaper Echelon',
  OBRME = 'Ossiarch Bonereapers Mortisan Elite',
  OgorSg = 'Ogor Mawtribes Scrapglutt',
  OgorTB = 'Ogor Mawtribes Tyrant\'s Bellow',
  SBC = 'Sylvaneth Bitterbark Copse',
  SBGBH = 'Soulblight Gravelords Bloodcrave Hunt',
  SBGDTH = 'Soulblight Gravelords Deathrattle Tomb Host',
  SEVB = 'Stormcase Eternals Vigilant Brotherhood',
  SEY = 'Stormcase Eternals Yndrasta',
  SGC = 'Skaven Gnawfeast Clawpack',
  SoBWS = 'Sons of Behemat Wallsmasher Stomp',
  SSB = 'Seraphon Sunblooded Prowlers',
  SSW = 'Seraphon Starscale Warhost',
  S2DBW = 'Slaves to Darkness Bloodwind Legion',
  S2DDR = 'Slaves to Darkness Darkoath Raiders',
  SWC = 'Skaven Warpspark Clawpack',
}

export const ARMY_CONFIG: Record<Army, ArmyConfig> = {
  [Army.BoKBGP]: {
    name: 'Bloodhound Gore Pilgrims',
    faction: 'Blades of Khorne',
    grandAlliance: 'Chaos',
    shortName: 'BoK',
    armyFields: [
      { type: 'integer', key: 'bloodTithePoints', label: 'Blood Tithe Points', min: 0 },
      { type: 'integer', key: 'score', label: 'Score', min: 0 },
    ],
    units: [],
  },
  [Army.BoKFotBG]: {
    name: 'Fangs of the Blood God',
    faction: 'Blades of Khorne',
    grandAlliance: 'Chaos',
    shortName: 'BoK',
    shortArmyName: 'Fangs',
    armyFields: [],
    units: [],
  },
  [Army.KOST]: {
    name: 'Skyhammer Taskforce',
    faction: 'Kharadron Overlords',
    grandAlliance: 'Order',
    shortName: 'KO',
    shortArmyName: 'Skyhammer',
    armyFields: [],
    units: [],
  },
  [Army.KOGT]: {
    name: 'Grundstok Trailblazers',
    faction: 'Kharadron Overlords',
    grandAlliance: 'Order',
    shortName: 'KO',
    shortArmyName: 'Grundstok',
    armyFields: [],
    units: [],
  },
  [Army.CosCC]: {
    name: 'Castelite Company',
    faction: 'Cities of Sigmar',
    grandAlliance: 'Order',
    shortName: 'CoS',
    armyFields: [],
    units: [],
  },
  [Army.CosFP]: {
    name: 'Fusil-Platoon',
    faction: 'Cities of Sigmar',
    grandAlliance: 'Order',
    shortName: 'CoS',
    armyFields: [],
    units: [],
  },
  [Army.DoKHT]: {
    name: 'Heartflayer Troupe',
    faction: 'Daughters of Kaine',
    grandAlliance: 'Order',
    shortName: 'DoK',
    armyFields: [],
    units: [],
  },
  [Army.DoTFC]: {
    name: 'Fluxblade Coven',
    faction: 'Disciples of Tzeentch',
    grandAlliance: 'Chaos',
    shortName: 'DoT',
    armyFields: [],
    units: [],
  },
  [Army.FECCR]: {
    name: 'Carrion Retainers',
    faction: 'Flesh-eater Courts',
    grandAlliance: 'Death',
    shortName: 'FeC',
    armyFields: [],
    units: [
      {
        id: 'abhorrentArchregent',
        name: 'Abhorrent Archregent',
        metaFields: [
          { type: 'integer', key: 'nobleDeedsPoints', label: 'Noble Deeds Points', min: 0, max: 6, thresholdTags: [ { value: 6, tag: '+1 Attacks', color: '#e91e63' } ] },
        ],
      },
      {
        id: 'varghulfCourtier',
        name: 'Varghulf Courtier',
        metaFields: [
          { type: 'integer', key: 'nobleDeedsPoints', label: 'Noble Deeds Points', min: 0, max: 6, thresholdTags: [ { value: 6, tag: '+1 Attacks', color: '#e91e63' } ] },
        ],
      },
    ],
  },
  [Army.FECCW]: {
    name: 'Charnel Watch',
    faction: 'Flesh-eater Courts',
    grandAlliance: 'Death',
    shortName: 'FeC',
    armyFields: [],
    units: [],
  },
  [Army.FSA]: {
    name: 'Saga Axeband',
    faction: 'Fyreslayers',
    grandAlliance: 'Order',
    shortName: 'FS',
    armyFields: [
      { type: 'boolean', key: 'runeOfFury', label: 'Rune of Fury' },
      { type: 'boolean', key: 'runeOfSearingHeat', label: 'Rune of Searing Heat' },
      { type: 'boolean', key: 'runeOfFieryDetermination', label: 'Rune of Fiery Determination' },
      { type: 'boolean', key: 'runeOfRelentlessZeal', label: 'Rune of Relentless Zeal' },
    ],
    units: [],
  },
  [Army.GSGBMM]: {
    name: 'Bad Moon Madmob',
    faction: 'Gloomspite Gitz',
    grandAlliance: 'Destruction',
    shortName: 'GSG',
    armyFields: [],
    units: [],
  },
  [Army.GSGSH]: {
    name: 'Snarlpack Huntaz',
    faction: 'Gloomspite Gitz',
    grandAlliance: 'Destruction',
    shortName: 'GSG',
    armyFields: [],
    units: [],
  },
  [Army.HoSBotLD]: {
    name: 'Blades of the Lurid Dream',
    faction: 'Hedonites of Slaanesh',
    grandAlliance: 'Chaos',
    shortName: 'HoS',
    armyFields: [
      { type: 'integer', key: 'depravityPoints', label: 'Depravity Points', min: 0, thresholdTags: [
        { value: 12, tag: 'Run & Charge', color: '#9c27b0' },
        { value: 18, tag: 'Crit (mortal)', color: '#e91e63' },
        { value: 24, tag: 'Ward 5+', color: '#2196f3' },
      ] },
      { type: 'integer', key: 'temptationDice', label: 'Temptation Dice', min: 0, max: 6, default: 6 },
    ],
    units: [],
  },
  [Army.HHHH]: {
    name: 'Helforge Host',
    faction: 'Helsmiths of Hashut',
    grandAlliance: 'Chaos',
    shortName: 'Helsmiths',
    shortArmyName: 'Helforge',
    armyFields: [],
    units: [
      {
        id: 'despot',
        name: 'War Despot',
        metaFields: [
          { type: 'integer', key: 'daemonicPowerPoints', label: 'Daemonic Power Points', min: 0, max: 3, default: 0, topTagOnly: true, thresholdTags: [
            { value: 0, tag: '6" Ability range', color: '#2196f3' },
            { value: 1, tag: '9" Ability range', color: '#2196f3' },
            { value: 2, tag: '12" Ability range', color: '#2196f3' },
            { value: 3, tag: '15" Ability range', color: '#2196f3' },
          ] },
        ]
      },
      {
        id: 'dominatorengine',
        name: 'Dominator Engine',
        metaFields: [
          { type: 'integer', key: 'daemonicPowerPoints', label: 'Daemonic Power Points', min: 0, max: 3, default: 0, topTagOnly: true, thresholdTags: [
            { value: 1, tag: '+1" Move, +1 Attack', color: '#2196f3' },
            { value: 2, tag: '+2" Move, +2 Attacks', color: '#2196f3' },
            { value: 3, tag: '+3" Move, +3 Attack', color: '#2196f3' },
          ] },
        ]
      },
      {
        id: 'tormentorbombard',
        name: 'Tormentor Bombard',
        metaFields: [
          { type: 'integer', key: 'daemonicPowerPoints', label: 'Daemonic Power Points', min: 0, max: 3, default: 0 },
        ]
      },
      {
        id: 'infernalcohort1',
        name: 'Infernal Cohort',
        metaFields: [
          { type: 'integer', key: 'daemonicPowerPoints', label: 'Daemonic Power Points', min: 0, max: 3, default: 0, topTagOnly: true, thresholdTags: [
            { value: 1, tag: 'Ward 6+', color: '#2196f3' },
            { value: 2, tag: 'Ward 5+', color: '#2196f3' },
            { value: 3, tag: 'Ward 5+, ignore first wound', color: '#2196f3' },
          ] },
        ]
      },
      {
        id: 'infernalcohort2',
        name: 'Infernal Cohort',
        metaFields: [
          { type: 'integer', key: 'daemonicPowerPoints', label: 'Daemonic Power Points', min: 0, max: 3, default: 0, topTagOnly: true, thresholdTags: [
            { value: 1, tag: 'Ward 6+', color: '#2196f3' },
            { value: 2, tag: 'Ward 5+', color: '#2196f3' },
            { value: 3, tag: 'Ward 5+, ignore first wound', color: '#2196f3' },
          ] },
        ]
      }
    ],
  },
  [Army.IDKSH]: {
    name: 'Soulraid Hunt',
    faction: 'Idoneth Deepkin',
    grandAlliance: 'Order',
    shortName: 'IDK',
    shortArmyName: 'Soulraid',
    armyFields: [],
    units: [],
  },
  [Army.IDKATG]: {
    name: 'Akhelian Tide Guard',
    faction: 'Idoneth Deepkin',
    grandAlliance: 'Order',
    shortName: 'IDK',
    shortArmyName: 'Tide Guard',
    armyFields: [],
    units: [],
  },
  [Army.IJBM]: {
    name: 'Ironjawz Bigmob',
    faction: 'Orruk Warclans',
    grandAlliance: 'Destruction',
    shortName: 'Orruk',
    armyFields: [],
    units: [],
  },
  [Army.KBSG]: {
    name: 'Swampskulka Gang',
    faction: 'Orruk Warclans',
    grandAlliance: 'Destruction',
    shortName: 'Orruk',
    shortArmyName: 'KB',
    armyFields: [],
    units: [],
  },
  [Army.LRLGP]: {
    name: 'Glittering Phalanx',
    faction: 'Lumineth Realm-lords',
    grandAlliance: 'Order',
    shortName: 'LRL',
    armyFields: [],
    units: [],
  },
  [Army.MoNBH]: {
    name: 'Bleak Host',
    faction: 'Maggotkin of Nurgle',
    grandAlliance: 'Chaos',
    shortName: 'MoN',
    armyFields: [
      { type: 'integer', key: 'diseasePoints', label: 'Disease Points', min: 0, max: 7 },
    ],
    units: [],
  },
  [Army.NHSH]: {
    name: 'Slasher Host',
    faction: 'Nighthaunt',
    grandAlliance: 'Death',
    shortName: 'NH',
    armyFields: [],
    units: [],
  },
  [Army.NHCS]: {
    name: 'Cursed Shaklehorde',
    faction: 'Nighthaunt',
    grandAlliance: 'Death',
    shortName: 'NH',
    shortArmyName: 'Shaklehorde',
    armyFields: [],
    units: [],
  },
  [Army.OBRME]: {
    name: 'Mortisan Elite',
    faction: 'Ossiarch Bonereapers',
    grandAlliance: 'Death',
    shortName: 'OBR',
    armyFields: [],
    units: [],
  },
  [Army.OBRTRE]: {
    name: 'Tithe-reaper Echelon',
    faction: 'Ossiarch Bonereapers',
    grandAlliance: 'Death',
    shortName: 'OBR',
    shortArmyName: 'Tithe-reaper',
    armyFields: [
      { type: 'integer', key: 'ossiarchCommandPoints', label: 'Ossiarch Command Points', min: 0, max: 2, default: 2 },
    ],
    units: [
      {
        id: 'gothizzarharvester',
        name: 'Gothizzar Harvester',
        metaFields: [
          { type: 'integer', key: 'boneTithePoints', label: 'Bone Tithe Points', min: 0, max: 6, default: 0 },
        ],
      }
    ],
  },
  [Army.OgorSg]: {
    name: 'Scrapglutt',
    faction: 'Ogor Mawtribes',
    grandAlliance: 'Destruction',
    shortName: 'Ogor',
    armyFields: [],
    units: [],
  },
  [Army.OgorTB]: {
    name: 'Tyrant\'s Bellow',
    faction: 'Ogor Mawtribes',
    grandAlliance: 'Destruction',
    shortName: 'Ogor',
    armyFields: [],
    units: [],
  },
  [Army.SBC]: {
    name: 'Bitterbark Copse',
    faction: 'Sylvaneth',
    grandAlliance: 'Order',
    shortName: 'SBC',
    armyFields: [],
    units: [],
  },
  [Army.SBGBH]: {
    name: 'Bloodcrave Hunt',
    faction: 'Soulblight Gravelords',
    grandAlliance: 'Death',
    shortName: 'SBG',
    shortArmyName: 'Bloodcrave',
    armyFields: [
      { type: 'integer', key: 'score', label: 'Score', min: 0 },
    ],
    units: [],
  },
  [Army.SBGDTH]: {
    name: 'Deathrattle Tomb Host',
    faction: 'Soulblight Gravelords',
    grandAlliance: 'Death',
    shortName: 'SBG',
    shortArmyName: 'Deathrattle',
    armyFields: [],
    units: [],
  },
  [Army.SEVB]: {
    name: 'Vigilant Brotherhood',
    faction: 'Stormcast Eternals',
    grandAlliance: 'Order',
    shortName: 'SCE',
    armyFields: [],
    units: [],
  },
  [Army.SEY]: {
    name: 'Yndrasta',
    faction: 'Stormcast Eternals',
    grandAlliance: 'Order',
    shortName: 'SCE',
    armyFields: [],
    units: [],
  },
  [Army.SGC]: {
    name: 'Gnawfeast Clawpack',
    faction: 'Skaven',
    grandAlliance: 'Chaos',
    shortName: 'Skaven',
    shortArmyName: 'Gnawfeast',
    armyFields: [],
    units: [],
  },
  [Army.SoBWS]: {
    name: 'Wallsmasher Stomp',
    faction: 'Sons of Behemat',
    grandAlliance: 'Destruction',
    shortName: 'SoB',
    armyFields: [],
    units: [],
  },
  [Army.SSB]: {
    name: 'Sunblooded Prowlers',
    faction: 'Seraphon',
    grandAlliance: 'Order',
    shortName: 'Seraphon',
    shortArmyName: 'Sunblooded',
    armyFields: [],
    units: [],
  },
  [Army.SSW]: {
    name: 'Starscale Warhost',
    faction: 'Seraphon',
    grandAlliance: 'Order',
    shortName: 'Seraphon',
    shortArmyName: 'Starscale',
    armyFields: [],
    units: [],
  },
  [Army.S2DBW]: {
    name: 'Bloodwind Legion',
    faction: 'Slaves to Darkness',
    grandAlliance: 'Chaos',
    shortName: 'S2D',
    armyFields: [],
    units: [
        {
          id: 'chaosLord',
          name: 'Chaos Lord',
          metaFields: [
            { type: 'boolean', key: 'wardOfTzeentch', label: 'Ward of Tzeentch', checkedTag: 'Ward 6+', tagColor: '#2196f3' },
            { type: 'boolean', key: 'graceOfSlaanesh', label: 'Grace of Slaanesh', checkedTag: '+1 Run', tagColor: '#9c27b0' },
            { type: 'boolean', key: 'blessingOfNurgle', label: 'Blessing of Nurgle', checkedTag: '-1 to Hit', tagColor: '#4caf50' },
            { type: 'boolean', key: 'furyOfKhorne', label: 'Fury of Khorne', checkedTag: '+1 Rend', tagColor: '#e91e63' },
          ],
        },
        {
          id: 'chaosChariot',
          name: 'Chaos Chariot',
          metaFields: [
            { type: 'boolean', key: 'wardOfTzeentch', label: 'Ward of Tzeentch', checkedTag: 'Ward 6+', tagColor: '#2196f3' },
            { type: 'boolean', key: 'graceOfSlaanesh', label: 'Grace of Slaanesh', checkedTag: '+1 Run', tagColor: '#9c27b0' },
            { type: 'boolean', key: 'blessingOfNurgle', label: 'Blessing of Nurgle', checkedTag: '-1 to Hit', tagColor: '#4caf50' },
            { type: 'boolean', key: 'furyOfKhorne', label: 'Fury of Khorne', checkedTag: '+1 Rend', tagColor: '#e91e63' },
          ],
        },
        {
          id: 'chaosWarriors1',
          name: 'Chaos Warriors',
          metaFields: [
            { type: 'boolean', key: 'wardOfTzeentch', label: 'Ward of Tzeentch', checkedTag: 'Ward 6+', tagColor: '#2196f3' },
            { type: 'boolean', key: 'graceOfSlaanesh', label: 'Grace of Slaanesh', checkedTag: '+1 Run', tagColor: '#9c27b0' },
            { type: 'boolean', key: 'blessingOfNurgle', label: 'Blessing of Nurgle', checkedTag: '-1 to Hit', tagColor: '#4caf50' },
            { type: 'boolean', key: 'furyOfKhorne', label: 'Fury of Khorne', checkedTag: '+1 Rend', tagColor: '#e91e63' },
          ],
        },
        {
          id: 'chaosWarriors2',
          name: 'Chaos Warriors',
          metaFields: [
            { type: 'boolean', key: 'wardOfTzeentch', label: 'Ward of Tzeentch', checkedTag: 'Ward 6+', tagColor: '#2196f3' },
            { type: 'boolean', key: 'graceOfSlaanesh', label: 'Grace of Slaanesh', checkedTag: '+1 Run', tagColor: '#9c27b0' },
            { type: 'boolean', key: 'blessingOfNurgle', label: 'Blessing of Nurgle', checkedTag: '-1 to Hit', tagColor: '#4caf50' },
            { type: 'boolean', key: 'furyOfKhorne', label: 'Fury of Khorne', checkedTag: '+1 Rend', tagColor: '#e91e63' },
          ],
        },
        {
          id: 'chaosKnights',
          name: 'Chaos Knights',
          metaFields: [
            { type: 'boolean', key: 'wardOfTzeentch', label: 'Ward of Tzeentch', checkedTag: 'Ward 6+', tagColor: '#2196f3' },
            { type: 'boolean', key: 'graceOfSlaanesh', label: 'Grace of Slaanesh', checkedTag: '+1 Run', tagColor: '#9c27b0' },
            { type: 'boolean', key: 'blessingOfNurgle', label: 'Blessing of Nurgle', checkedTag: '-1 to Hit', tagColor: '#4caf50' },
            { type: 'boolean', key: 'furyOfKhorne', label: 'Fury of Khorne', checkedTag: '+1 Rend', tagColor: '#e91e63' },
          ],
        },
      ],
  },
  [Army.S2DDR]: {
    name: 'Darkoath Raiders',
    faction: 'Slaves to Darkness',
    grandAlliance: 'Chaos',
    shortName: 'S2D',
    armyFields: [],
    units: [],
  },
  [Army.SWC]: {
    name: 'Warpspark Clawpack',
    faction: 'Skaven',
    grandAlliance: 'Chaos',
    shortName: 'Skaven',
    shortArmyName: 'Warpspark',
    armyFields: [],
    units: [],
  },
};

// Helper function to create default state from config
export function getDefaultPlayerMeta(army: Army | undefined): PlayerMeta {
  if (!army) {
    return {
      notes: '',
      armyFields: [],
      units: [],
    };
  }

  const config = ARMY_CONFIG[army];
  return {
    notes: '',
    armyFields: (config.armyFields || []).map(field => ({
      key: field.key,
      value: field.type === 'integer'
        ? (typeof field.default === 'number' ? field.default : 0)
        : false,
    })),
    units: (config.units || []).map(unit => ({
      id: unit.id,
      label: '',
      metaFields: unit.metaFields.map(field => ({
        key: field.key,
        value: field.type === 'integer'
          ? (typeof field.default === 'number' ? field.default : 0)
          : false,
      })),
    })),
  };
} 
